import { Button, Input, Sheet } from "@mui/joy";
import { Box, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import {
  accountKitApi,
  useUpdateCurrentUserMutation,
} from "@nerdjs/account-kit";
import { NerdPhoneField } from "@nerdjs/nerd-ui";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formOpenSelector, setFormOpen } from "../../reducers/rhapsody";
import { useUpdateMemberMutation } from "../../redux/member/memberEndpoints";
import { useMyMember } from "../../redux/member/memberHooks";
import { useCurrentUser } from "../../redux/user/userHook";
import { useValidateVerificationCodeMutation } from "../../redux/verificationCode/verificationCodeHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";
import DialogClose from "../dialogClose/dialogClose";
import {
  DialogHeader,
  FieldLabel,
  GroupCaption,
  dialogPaperSx,
  groupSheetSx,
  pillButtonSx,
  tileColors,
} from "../formKit/formKit";

enum PasswordStrength {
  weak,
  average,
  strong,
}

/**
 *
 * @returns {ReactElement} SmsNotifications page
 */
export function SmsNotifications() {
  const open = useSelector(formOpenSelector("smsNotifications"));
  const dispatch = useDispatch();
  const { me: data } = useCurrentUser();

  const { member } = useMyMember();
  const [updateMember] = useUpdateMemberMutation();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const [validateCode] = useValidateVerificationCodeMutation();
  const [phone, setPhone] = useState(data?.phone ?? "");
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setPhone(data?.phone ?? "");
  }, [data]);

  const askQuestion = useAskQuestion();

  const onClose = () => {
    setError("");
    setAskCode(false);
    setCode("");
    dispatch(setFormOpen({ formID: "smsNotifications", isOpen: false }));
  };

  return (
    <>
      <DialogClose
        open={open}
        onClose={onClose}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>
          <DialogHeader
            title="SMS Notifications"
            subtitle="Receive text messages on new calls."
          />
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minWidth: 450,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <img
              style={{ width: 280, borderRadius: 14 }}
              src={
                "https://storage.googleapis.com/rhapsody/logos/sms_notification.png"
              }
            />
          </Box>
          <GroupCaption>Your number</GroupCaption>
          <Sheet variant="soft" sx={groupSheetSx}>
            <Box
              sx={{
                display: "flex",
                pl: 1.5,
                pr: 1,
                alignItems: "center",
                height: 44,
                justifyContent: "space-between",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <FieldLabel
                icon="fa-solid fa-phone"
                color={tileColors.green}
                label="Phone Number"
              />
              <Box
                sx={{
                  width: 200,
                  height: 40,
                  position: "absolute",
                  top: 6,
                  right: 8,
                }}
                className="musician"
              >
                <NerdPhoneField
                  value={phone}
                  onAccept={(e) => {
                    setPhone(e);
                  }}
                  textFieldProps={{
                    fullWidth: true,
                    variant: "standard",
                    inputProps: {
                      border: "none",
                      disableUnderline: true,
                      style: { textAlign: "right" },
                    },
                    size: "small",
                    sx: { background: "transparent" },
                  }}
                />
              </Box>
            </Box>
          </Sheet>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            variant="plain"
            color="neutral"
            sx={pillButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!data.phone && phone) {
                return updateCurrentUser({ phone })
                  .unwrap()
                  .then((e) => {
                    setAskCode(true);
                  })
                  .catch((e) => {
                    setError(e?.data?.description);
                  });
              }
              updateMember({
                id: member.id,
                body: { ...member, contactBySMS: !member.contactBySMS },
              });
              onClose();
            }}
            variant="solid"
            color="primary"
            sx={{ ...pillButtonSx, px: 3 }}
          >
            {member.contactBySMS ? "Pause" : "Enroll"}
          </Button>
        </DialogActions>
      </DialogClose>
      <DialogClose
        open={askCode}
        onClose={() => setAskCode(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>
          <DialogHeader
            title="Enter the 6 digits code:"
            subtitle={`Sent to ${phone}`}
          />
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minWidth: 360,
          }}
        >
          <GroupCaption>Verification</GroupCaption>
          <Sheet variant="soft" sx={groupSheetSx}>
            <Input
              variant="plain"
              sx={{ background: "transparent" }}
              slotProps={{
                input: { style: { textAlign: "right" } },
              }}
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-message"
                  color={tileColors.green}
                  label="Code"
                />
              }
              placeholder="6 digits code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              size="lg"
            />
          </Sheet>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setAskCode(false);
              setPhone(data.phone);
            }}
            color="neutral"
            variant="plain"
            sx={pillButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setAskCode(false);
              setPhone(data.phone);
              validateCode({ kind: "PHONE", value: phone })
                .unwrap()
                .then(() => {
                  dispatch(accountKitApi.util.invalidateTags(["user"]));
                  updateMember({
                    id: member.id,
                    body: { ...member, contactBySMS: true },
                  });
                })
                .catch((e) => {
                  setError(e?.data?.description);
                });
            }}
            variant="solid"
            sx={{ ...pillButtonSx, px: 3 }}
          >
            Verify
          </Button>
        </DialogActions>
      </DialogClose>
    </>
  );
}
